import type { TScheduleDay } from '~/types/profileClinic'

export const SCHEDULE_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

const SHORT_LABELS: Record<string, string> = {
  mon: 'Пн',
  tue: 'Вт',
  wed: 'Ср',
  thu: 'Чт',
  fri: 'Пт',
  sat: 'Сб',
  sun: 'Вс',
}

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'] as const
const WEEKENDS = ['sat', 'sun'] as const

type ScheduleDayInput = Pick<TScheduleDay, 'day' | 'isHoliday' | 'timeStart' | 'timeEnd'>

function padTime(time: string): string {
  const match = time.trim().match(/^(\d{1,2}):(\d{2})/)
  if (!match) {
    return time.trim()
  }
  return `${match[1].padStart(2, '0')}:${match[2]}`
}

function signature(day: ScheduleDayInput): string {
  if (day.isHoliday) {
    return 'holiday'
  }
  return `${padTime(day.timeStart)}-${padTime(day.timeEnd)}`
}

function isRoundTheClock(day: ScheduleDayInput): boolean {
  if (day.isHoliday) {
    return false
  }
  const start = padTime(day.timeStart)
  const end = padTime(day.timeEnd)
  return start === '00:00' && (end === '23:59' || end === '24:00')
}

function formatLine(label: string, day: ScheduleDayInput): string {
  if (day.isHoliday) {
    return `${label} выходной`
  }
  if (isRoundTheClock(day)) {
    return `${label} круглосуточно`
  }
  return `${label} с ${padTime(day.timeStart)} до ${padTime(day.timeEnd)}`
}

function usableDays(days: ScheduleDayInput[]): ScheduleDayInput[] {
  const byDay = new Map<string, ScheduleDayInput>()
  for (const day of days) {
    if (SCHEDULE_DAY_KEYS.includes(day.day as typeof SCHEDULE_DAY_KEYS[number])) {
      byDay.set(day.day, day)
    }
  }
  return SCHEDULE_DAY_KEYS
    .map((key) => byDay.get(key))
    .filter((day): day is ScheduleDayInput => {
      if (!day) {
        return false
      }
      if (day.isHoliday) {
        return true
      }
      return Boolean(day.timeStart && day.timeEnd)
    })
}

function sameSignature(days: ScheduleDayInput[]): boolean {
  if (!days.length) {
    return false
  }
  const first = signature(days[0])
  return days.every((day) => signature(day) === first)
}

function daysIn(days: ScheduleDayInput[], keys: readonly string[]): ScheduleDayInput[] {
  return days.filter((day) => keys.includes(day.day))
}

function areConsecutive(left: string, right: string): boolean {
  const from = SCHEDULE_DAY_KEYS.indexOf(left as typeof SCHEDULE_DAY_KEYS[number])
  const to = SCHEDULE_DAY_KEYS.indexOf(right as typeof SCHEDULE_DAY_KEYS[number])
  return from !== -1 && to === from + 1
}

function formatConsecutiveGroups(days: ScheduleDayInput[]): string[] {
  const groups: { days: string[], entry: ScheduleDayInput }[] = []

  for (const entry of days) {
    const last = groups[groups.length - 1]
    const canMerge = last
      && signature(last.entry) === signature(entry)
      && areConsecutive(last.days[last.days.length - 1], entry.day)

    if (canMerge && last) {
      last.days.push(entry.day)
      continue
    }

    groups.push({ days: [entry.day], entry })
  }

  return groups.map((group) => {
    const first = group.days[0]
    const last = group.days[group.days.length - 1]
    const label = group.days.length > 1
      ? `${SHORT_LABELS[first]}-${SHORT_LABELS[last]}`
      : SHORT_LABELS[first]
    return formatLine(label, group.entry)
  })
}

/** Публичная подпись графика: «Ежедневно с 09:00 до 21:00» или «Будни… / Выходные…». */
export function formatClinicScheduleLabel(
  days: ScheduleDayInput[] | null | undefined,
  isAlwaysOpen: boolean,
): string {
  if (isAlwaysOpen) {
    return 'Ежедневно круглосуточно'
  }

  const usable = usableDays(days ?? [])
  if (!usable.length) {
    return ''
  }

  if (usable.length === 7 && sameSignature(usable)) {
    return formatLine('Ежедневно', usable[0])
  }

  const weekdays = daysIn(usable, WEEKDAYS)
  const weekends = daysIn(usable, WEEKENDS)
  const weekdayLines = weekdays.length === 5 && sameSignature(weekdays)
    ? [formatLine('Будни', weekdays[0])]
    : formatConsecutiveGroups(weekdays)
  const weekendLines = weekends.length === 2 && sameSignature(weekends)
    ? [formatLine('Выходные', weekends[0])]
    : formatConsecutiveGroups(weekends)

  return [...weekdayLines, ...weekendLines].join('\n')
}

export function clinicSchedulePlain(schedule: string): string {
  return schedule.replace(/<br\s*\/?>/gi, '\n')
}
